import { lazy, Suspense } from 'react';
import {
  createBrowserRouter,
  createRoutesFromElements,
  Navigate,
  Route
} from 'react-router';
import { RootErrorBoundary, Loader } from '@link/share/components';
import { buildLocalizedPath } from '@link/share/utils';
import i18n from '@link/triplabo/i18n';
import { TriplaboLayout } from '@link/triplabo/layouts';
import PrivateRoute from './PrivateRoute';
import styles from './loader.module.css';

const Shinkansen = lazy(() => import('@link/triplabo/views/Shinkansen'));
const ShinkansenProduct = lazy(
  () => import('@link/triplabo/views/Shinkansen/Product')
);
const BookConfirm = lazy(() => import('@link/triplabo/views/Book/BookConfirm'));
const BookResult = lazy(() => import('@link/triplabo/views/Book/BookResult'));
const Booking = lazy(() => import('@link/triplabo/views/Booking'));
const BookingDetail = lazy(
  () => import('@link/triplabo/views/Booking/BookingDetail')
);
const Profile = lazy(() => import('@link/triplabo/views/Profile'));
const ChangePassword = lazy(
  () => import('@link/triplabo/views/ChangePassword')
);
const ResetPassword = lazy(() => import('@link/triplabo/views/ResetPassword'));
const Login = lazy(() => import('@link/triplabo/views/Login'));
const Privacy = lazy(() => import('@link/share/views/Privacy'));
const FAQ = lazy(() => import('@link/share/views/FAQ'));
const Terms = lazy(() => import('@link/share/views/Terms'));
const Law = lazy(() => import('@link/triplabo/views/Law'));
const NotFound = lazy(() => import('@link/triplabo/views/NotFound'));

const lazyView = (view: JSX.Element) => {
  return (
    <Suspense fallback={<Loader className={styles.loader} />}>{view}</Suspense>
  );
};

const router = createBrowserRouter(
  createRoutesFromElements(
    <Route errorElement={<RootErrorBoundary />}>
      <Route>
        <Route path="/:lang" element={<TriplaboLayout />}>
          <Route index element={<Navigate to="shinkansen" replace />} />
          <Route element={<PrivateRoute />}>
            <Route
              path="book/confirm"
              element={lazyView(<BookConfirm />)}
              handle={{ disableLang: true }}
            />
            <Route
              path="book/result/:bookingId"
              element={lazyView(<BookResult />)}
              handle={{ disableLang: true }}
            />
            <Route
              path="booking"
              element={lazyView(<Booking />)}
              handle={{
                title: i18n.t('views.booking.title')
              }}
            />
            <Route
              path="booking/:bookingId"
              element={lazyView(<BookingDetail />)}
            />
            <Route
              path="profile"
              element={lazyView(<Profile />)}
              handle={{ title: i18n.t('share.profile.title') }}
            />
            <Route
              path="change-password"
              element={lazyView(<ChangePassword />)}
              handle={{ title: i18n.t('share.password.changePassword') }}
            />
          </Route>
          <Route
            path="shinkansen"
            element={lazyView(<Shinkansen />)}
            handle={{ title: i18n.t('views.shinkansen.title') }}
          />
          <Route
            path="shinkansen/product"
            element={lazyView(<ShinkansenProduct />)}
            handle={{ title: i18n.t('views.shinkansen.title') }}
          />
          <Route
            path="login"
            element={lazyView(<Login />)}
            handle={{ title: i18n.t('share.login.title') }}
          />
          <Route
            path="privacy"
            element={lazyView(<Privacy />)}
            handle={{ title: i18n.t('views.privacy.title') }}
          />
          <Route
            path="faq"
            element={lazyView(<FAQ />)}
            handle={{ title: i18n.t('views.faq.title') }}
          />
          <Route
            path="terms"
            element={lazyView(<Terms />)}
            handle={{ title: i18n.t('views.terms.title') }}
          />
          <Route
            path="law"
            element={lazyView(<Law />)}
            handle={{ title: i18n.t('law.title') }}
          />
          <Route
            path="reset-password"
            element={lazyView(<ResetPassword />)}
            handle={{ title: i18n.t('share.password.resetPassword') }}
          />

          <Route path="*" element={lazyView(<NotFound />)} />
        </Route>
      </Route>
      <Route
        path="/"
        element={<Navigate to={buildLocalizedPath()} replace />}
      />
    </Route>
  )
);

export default router;
